import React from 'react';
import type { ReactNode } from 'react';
import type { IconType } from './base_icons';
import {
  NoPaddingStrongYesIcon as StrongYesIcon,
  NoPaddingYesIcon as YesIcon,
  NoPaddingNeutralIcon as NeutralIcon,
  NoPaddingNoIcon as NoIcon,
  NoPaddingStrongNoIcon as StrongNoIcon,
  NoPaddingNoDecisionIcon as NoDecisionIcon,
} from './base_icons';
import {
  NeutralTag,
  WarningTag,
} from '@grnhse/seedling/lib/birch/components/tags';
import { CompactRow } from '@grnhse/seedling/lib/birch/layouts/compact_row';
import { Tooltip } from '@grnhse/seedling/lib/birch/components/tooltips';
import { withLocalePrefix } from '../../utils/translation';

const t = withLocalePrefix('scorecard.ratings');

type ScorecardIconProps = {
  type: IconType;
  name?: ReactNode;
  title?: string;
  className?: string;
  edited?: boolean;
  interviewer_name?: string | null;
};

export const ScorecardIcons = ({
  type,
  name,
  title,
  className,
  edited = false,
  interviewer_name,
}: ScorecardIconProps) => {
  let component;

  switch (type) {
    case 'strong_no':
      component = <StrongNoIcon title={title} />;
      break;
    case 'no':
      component = <NoIcon title={title} />;
      break;
    case 'neutral':
      component = <NeutralIcon title={title} />;
      break;
    case 'yes':
      component = <YesIcon title={title} />;
      break;
    case 'strong_yes':
      component = <StrongYesIcon title={title} />;
      break;
    case 'no_decision':
      component = <NoDecisionIcon title={title} />;
      break;
    default:
      component = null;
      break;
  }

  if (!name) {
    if (edited) {
      return (
        <CompactRow>
          {component}
          <EditedBadge />
        </CompactRow>
      );
    }
    return component;
  }

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '4px',
      }}
      className={className}
    >
      {component}
      <CompactRow>
        <div>
          <span className="name">{name}</span>
          {interviewer_name && <span>{`, ${interviewer_name}`}</span>}
        </div>

        {edited && <EditedBadge />}
      </CompactRow>
    </span>
  );
};

export const EditedBadge = () => {
  return (
    <Tooltip label={t('edited_tooltip')} ariaLabel={t('edited_tooltip')}>
      <NeutralTag text={t('edited')} />
    </Tooltip>
  );
};

export const TransferredBadge = () => {
  return (
    <Tooltip
      label={t('transferred_tooltip')}
      ariaLabel={t('transferred_tooltip')}
    >
      <WarningTag text={t('transferred')} />
    </Tooltip>
  );
};
